import { useMemo } from "react";
import { apiFetch } from "../../toolympus/api/core";
import { useEditItem2 } from "../../toolympus/api/useNewItem";
import { exportRecordsToXlsxSheet } from "../../toolympus/api/xlsx";
import { FormField } from "../../toolympus/components/ConfigurableForms/types";
import { FieldType, mergeSchema, Schema } from "../../toolympus/hooks/useSchema";
import { ApplicationRecord } from "./typings";
import { utc } from "../../toolympus/components/timezone";
import { useFormats } from "../../toolympus/components/schemed";
import { DateTimeInternalFormat } from "../../toolympus/api/datetimeUtil";

const ExportSavedFieldsSelectionLsKey = "__pl_applications_export_fields";

const saveSelectedFieldsToLs = (fields: string[]) => {
  localStorage.setItem(ExportSavedFieldsSelectionLsKey, fields.join(","));
}

const readSelectedFieldsFromLs = (): string[] => {
  return (localStorage.getItem(ExportSavedFieldsSelectionLsKey) || "").split(",").map(s => s.trim()).filter(x => !!x);
}

export const useApplicationsExport = (apiPath: string, fields: FormField[], schema: Schema, filters: [string,string][]) => {


  const getFieldsSuitableForExport = (fields: FormField[]) => {
    return fields
      .filter(f => f.fieldtype === "text" 
        || f.fieldtype === "textlong" 
        || f.fieldtype === "select_radio" 
        || f.fieldtype === "boolean" 
        || f.fieldtype === "email");
  }

  const { formatDatetimeWithFormat } = useFormats();


  const exportApplications = useEditItem2<{}>({
    save: async (item) => {
      const params = filters.filter(([k,v]) => !!v)
        .map(([k,v]) => `${k}=${v}`)
        .join("&");
      const allRecords = await apiFetch<ApplicationRecord[]>(`${apiPath}?${params}`);

      const columns = Object.entries(item).filter(([k,v]) => v === true).map(([k,]) => k);
      saveSelectedFieldsToLs(columns);

      if(columns.length) {
        await exportRecordsToXlsxSheet({
          filename: "applications.xlsx",
          prepare: r => ({
            ...r.info,
            created_at: formatDatetimeWithFormat(utc.toLocal(r.created_at), DateTimeInternalFormat),
            // created_at: (moment(r.created_at).valueOf() / 86400000) + 25569,
          }),
          schema: mergeSchema(schema, { created_at: { label: "Создана" }}),
          columns: [...columns, "created_at"],
        }, allRecords);
      }

      return item;
    }
  })

  const [exportSuitableFields, exportFieldsSchema] = useMemo(() => {
    const suitableFields = getFieldsSuitableForExport(fields);
    const schema = suitableFields.reduce<Schema>((r,f) => {
      r[f._id] = { type: FieldType.bool, label: f.title };
      return r;
    }, {});
    return [suitableFields, schema];
  }, [fields]);

  const startExport = () => {
    let fieldsSelected = readSelectedFieldsFromLs();
    if(!fieldsSelected.length) {
      fieldsSelected = exportSuitableFields.map(f => f._id);
    }
    exportApplications.startEditing(fieldsSelected.reduce<Record<string, boolean>>((r,f) => { r[f] = true; return r; }, {}));
  }

  return {
    ...exportApplications,
    start: startExport,
    exportFieldsSchema,
    exportSuitableFields,
  }
}
